import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import {
  FeedbackFilter,
  PaginationButtons,
  SearchInput,
} from "../../../components";
import clsx from "clsx";
import { PulseLoader } from "react-spinners";
import { useQuery } from "@tanstack/react-query";
import {
  requestFeedback,
  requestViewFeedback,
} from "../../../services/feedback.service";
import FeedbackItem from "../../../components/FeedbackItem";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowSmallDownIcon, FilterIcon } from "../../../assets";
import { useSearchParams } from "react-router-dom";
import { useDebounce, useOutsideClick } from "../../../hooks";

const Feedback: FC = (): JSX.Element => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [filterSelectedVersions, setFilterSelectedVersions] = useState<
    string[]
  >([]);

  const [nameSort, setNameSort] = useState(0);
  const [emailSort, setEmailSort] = useState(0);
  const [versionSort, setVersionSort] = useState(0);
  const [dateSort, setDateSort] = useState(0);

  const [filterTypes, setFilterTypes] = useState<string[]>([]);

  const onChangePage = (page: number) => {
    searchParams.set("page", page?.toString());
    setSearchParams(searchParams, { replace: true });
  };

  const debounceSearch = useDebounce(search, 600);

  const filterRef = useRef<HTMLDivElement>(null);
  useOutsideClick(filterRef, () => setIsOpenFilter(false));

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    onChangePage(1);
    setSearch(e.target.value);
  };

  const { data, isPending } = useQuery({
    queryKey: [
      "feedback",
      page,
      filterSelectedVersions,
      filterTypes,
      debounceSearch,
      nameSort,
      emailSort,
      versionSort,
      dateSort,
    ],
    queryFn: async () => {
      return await requestFeedback({
        page: page - 1,
        limit: 20,
        area: filterTypes,
        appVersion: filterSelectedVersions,
        search: debounceSearch,
        sortValue:
          nameSort !== 0
            ? "firstName"
            : emailSort !== 0
            ? "email"
            : dateSort !== 0
            ? "createdAt"
            : versionSort !== 0
            ? "appVersion"
            : null,
        order:
          nameSort !== 0
            ? nameSort
            : emailSort !== 0
            ? emailSort
            : dateSort !== 0
            ? dateSort
            : versionSort !== 0
            ? versionSort
            : null,
      });
    },
  });

  const pageCount = data ? Math.ceil(data?.total / 20) : 0;

  const queryClient = useQueryClient();
  const { mutate: viewFeedback } = useMutation({
    mutationFn: requestViewFeedback,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["feedback"] });
    },
  });

  useEffect(() => {
    const notReadIds = data?.subscriptionFeedbacks
      .filter((item) => !item.reviewed)
      .map((item) => item.id);
    if (notReadIds?.length) {
      viewFeedback({ ids: notReadIds });
    }
  }, [data]);

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h4 className={styles.title}>Feedback</h4>
        <div className={styles.headerAddButton}></div>
      </header>

      <section className={styles.contentContainer}>
        <div className={styles.contentContainerHeader}>
          <div className={styles.searchContainer}>
            <SearchInput
              placeholder="Search"
              value={search}
              onChange={onChangeSearch}
            />
          </div>
          <div ref={filterRef}>
            <div
              onClick={() => setIsOpenFilter((prev) => !prev)}
              className={clsx(styles.filterButton, {
                [styles.activeFilterButton]: isOpenFilter,
              })}
            >
              <FilterIcon />
            </div>
            {isOpenFilter && (
              <FeedbackFilter
                filterSelectedVersions={filterSelectedVersions}
                setFilterSelectedVersions={(versions) => {
                  onChangePage(1);
                  setFilterSelectedVersions(versions);
                }}
                filterTypes={filterTypes}
                setFilterTypes={(types) => {
                  onChangePage(1);
                  setFilterTypes(types);
                }}
                setIsOpen={setIsOpenFilter}
              />
            )}
          </div>
        </div>

        <div className={styles.sectionHeader}>
          <div
            onClick={() => {
              setEmailSort(0);
              setVersionSort(0);
              setDateSort(0);
              setNameSort((prev) => {
                if (prev === 0) {
                  return -1;
                } else if (prev === -1) {
                  return 1;
                } else return 0;
              });
            }}
            className={clsx(styles.reporterBlock, {
              [styles.activeSortValue]: nameSort !== 0,
            })}
          >
            <p>User</p>{" "}
            <ArrowSmallDownIcon
              className={clsx(styles.sectionHeaderArrow, {
                [styles.sectionHeaderArrowUp]: nameSort === -1,
              })}
            />
          </div>
          <div className={clsx(styles.typeBlock)}>
            <p>Type</p>{" "}
          </div>
          <div className={clsx(styles.categoryBlock)}>
            <p>Feedback</p>{" "}
          </div>
          <div
            onClick={() => {
              setEmailSort(0);
              setNameSort(0);
              setDateSort(0);
              setVersionSort((prev) => {
                if (prev === 0) {
                  return -1;
                } else if (prev === -1) {
                  return 1;
                } else return 0;
              });
            }}
            className={clsx(styles.reportedOnBlock, {
              [styles.activeSortValue]: versionSort !== 0,
            })}
          >
            <p>App Version</p>{" "}
            <ArrowSmallDownIcon
              className={clsx(styles.sectionHeaderArrow, {
                [styles.sectionHeaderArrowUp]: versionSort === -1,
              })}
            />
          </div>
          <div
            onClick={() => {
              setEmailSort(0);
              setNameSort(0);
              setVersionSort(0);
              setDateSort((prev) => {
                if (prev === 0) {
                  return 1;
                } else if (prev === 1) {
                  return -1;
                } else return 0;
              });
            }}
            className={clsx(styles.dateBlock, {
              [styles.activeSortValue]: dateSort !== 0,
            })}
          >
            <p>Feedback date</p>{" "}
            <ArrowSmallDownIcon
              className={clsx(styles.sectionHeaderArrow, {
                [styles.sectionHeaderArrowUp]: dateSort === -1,
              })}
            />
          </div>
        </div>

        <div className={styles.listContainer}>
          {isPending ? (
            <div className={styles.loaderWrapper}>
              <PulseLoader color={"#7D4AFB"} />
            </div>
          ) : (
            data?.subscriptionFeedbacks.map((item, index) => (
              <FeedbackItem key={`${item.id}-${index}`} item={item} />
            ))
          )}
        </div>
        <PaginationButtons
          pagesCount={pageCount}
          page={page}
          onPress={(index) => setPage(index)}
        />
      </section>
    </div>
  );
};

export default Feedback;
